import { useState } from "react";
import {
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import { FaPencil } from "react-icons/fa6";
import withRouter from "hoc/withRouter";
import ListPage from "components/ListPage/ListPage";
import { snack } from "components/Snack/Snack";
import { useGetResponseTemplates } from "hooks/useGetResponseTemplates";
import responseTemplatesService from "services/admin/responseTemplatesService";
import {
  ResponseTemplate,
  ResponseTemplateLanguage,
  RESPONSE_TEMPLATE_LANGUAGES,
  RESPONSE_TEMPLATE_LANGUAGE_LABELS,
} from "interfaces/entities/responseTemplate.model";

// Обрезаем длинный контент в превью таблицы. Полный текст — на странице редактирования.
const truncate = (text: string, max = 100) =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

const ResponseTemplateList = () => {
  const [language, setLanguage] = useState<ResponseTemplateLanguage | "ALL">("ALL");
  const [search, setSearch] = useState("");

  const {
    data: templates,
    isLoading,
    refetch,
  } = useGetResponseTemplates({
    ...(language !== "ALL" ? { language } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });

  console.log("Loaded templates:", templates);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Удалить шаблон? Это действие необратимо.")) return;
    try {
      await responseTemplatesService.delete(id);
      snack("Шаблон удалён");
      refetch();
    } catch (e) {
      console.error(e);
      snack("Не удалось удалить шаблон");
    }
  };

  function buildContent() {
    if (isLoading) {
      return (
        <div className="text-center">
          <CircularProgress />
        </div>
      );
    }
    if (!templates || templates.length === 0) {
      return (
        <div>Шаблонов ответов нет. Чтобы добавить шаблон, нажмите кнопку «Добавить» сверху.</div>
      );
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Название</TableCell>
            <TableCell>Язык</TableCell>
            <TableCell>Текст</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map((t: ResponseTemplate, index: number) => (
            <TableRow key={t.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{t.title}</TableCell>
              <TableCell>
                <Chip
                  label={RESPONSE_TEMPLATE_LANGUAGE_LABELS[t.language] || t.language}
                  size="small"
                />
              </TableCell>
              <TableCell style={{ maxWidth: 480 }}>
                <span style={{ whiteSpace: "pre-wrap", color: "#555" }}>{truncate(t.content)}</span>
              </TableCell>
              <TableCell>
                <div className="flex gap-2 items-center">
                  <Tooltip title="Редактировать">
                    <Link to={`/admin/tickets/templates/edit/${t.id}`}>
                      <FaPencil />
                    </Link>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton color="error" onClick={() => handleDelete(t.id)}>
                      <FaTrashAlt />
                    </IconButton>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <ListPage pageTitle="Шаблоны ответов" pathToAdd="/admin/tickets/templates/add">
      <div className="m-t-16 flex gap-4 items-center flex-wrap">
        <TextField
          size="small"
          label="Поиск по названию"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <Select
          size="small"
          value={language}
          onChange={(e) => setLanguage(e.target.value as ResponseTemplateLanguage | "ALL")}
          style={{ minWidth: 160 }}
        >
          <MenuItem value="ALL">Все языки</MenuItem>
          {RESPONSE_TEMPLATE_LANGUAGES.map((lang) => (
            <MenuItem key={lang} value={lang}>
              {RESPONSE_TEMPLATE_LANGUAGE_LABELS[lang]}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="m-t-32">{buildContent()}</div>
    </ListPage>
  );
};

export default withRouter(ResponseTemplateList);
